import {
  PROBLEM_DIFFICULTIES,
  type ProblemDifficulty,
} from "@workspace/gamja-platform/types/difficulty";

/** 생성 폼의 입력 상태. 실제 후보 조회/배정 계약은 JCG-3371에서 연결한다. */
export interface QuizDraft {
  source: "wrong" | "saved" | "all";
  statuses: string[];
  count: number;
  theoryCount: number;
  subjects: string[];
  languages: string[];
  probabilities: string[];
  difficulties: ProblemDifficulty[];
  calculationOnly: boolean;
  past: "all" | "only" | "exclude";
  years: string[];
  includeBasic: boolean;
}

export const THEORY_OPTIONS = [
  ["db", "DB"],
  ["network-os", "네트워크·OS"],
  ["sw-design", "SW 설계"],
  ["sw-dev", "SW 개발"],
  ["security-newtech", "보안·신기술"],
] as const;
export const LANGUAGE_OPTIONS = [
  ["c-language", "C"],
  ["java", "Java"],
  ["python", "Python"],
  ["sql", "SQL"],
] as const;
export const PROBABILITY_OPTIONS = ["99.9%", "80%", "50%", "20%"];

export function recommendedDraft(): QuizDraft {
  return {
    source: "wrong",
    statuses: ["wrong", "unsure", "graduated"],
    count: 20,
    theoryCount: 10,
    subjects: THEORY_OPTIONS.map(([id]) => id),
    languages: LANGUAGE_OPTIONS.map(([id]) => id),
    probabilities: [...PROBABILITY_OPTIONS],
    difficulties: [...PROBLEM_DIFFICULTIES],
    calculationOnly: false,
    past: "all",
    years: [],
    includeBasic: true,
  };
}

export function resizeDraft(draft: QuizDraft, count: number): QuizDraft {
  const nextCount = Math.max(1, Math.min(20, Math.round(count)));
  return {
    ...draft,
    count: nextCount,
    theoryCount: Math.round((nextCount * draft.theoryCount) / draft.count),
  };
}

/** 비활성화된 값은 후속 출제/저장 콜백에도 전달하지 않는다. */
export function effectiveDraft(draft: QuizDraft): QuizDraft {
  return {
    ...draft,
    statuses: draft.statuses.filter(
      (s) => draft.source !== "wrong" || s !== "unattempted",
    ),
    subjects: draft.theoryCount > 0 ? draft.subjects : [],
    languages: draft.theoryCount < draft.count ? draft.languages : [],
    calculationOnly: draft.theoryCount > 0 && draft.calculationOnly,
    years: draft.past === "only" ? draft.years : [],
    includeBasic: draft.past !== "only" && draft.includeBasic,
  };
}

export function draftError(draft: QuizDraft): string | null {
  const value = effectiveDraft(draft);
  if (value.past === "only" && !value.years.length)
    return "기출 연도를 하나 이상 선택해 주세요.";
  if (!value.statuses.length) return "이해도를 하나 이상 선택해 주세요.";
  if (value.theoryCount && !value.subjects.length)
    return "이론 과목을 하나 이상 선택해 주세요.";
  if (value.count > value.theoryCount && !value.languages.length)
    return "코딩 언어를 하나 이상 선택해 주세요.";
  if (!value.difficulties.length) return "난이도를 하나 이상 선택해 주세요.";
  if (!value.probabilities.length) return "출제확률을 하나 이상 선택해 주세요.";
  return null;
}

export const PROBABILITY_EMOJIS: Record<string, string> = {
  "99.9%": "💯",
  "80%": "⭐️",
  "50%": "🔥",
  "20%": "🤔",
};
